"use client";

import { useState, useCallback } from "react";
import { AlertCircle } from "lucide-react";
import { payrollApi, type CalculationResponse } from "@/lib/api";
import {
  formatInputValue,
  parseInputValue,
  formatMoney as fmt,
  formatPercent as fmtPct,
} from "@/lib/format";

export function PAYECalculator() {
  const [taxableAmount, setTaxableAmount] = useState<string>("");
  const [insuranceRelief, setInsuranceRelief] = useState<string>("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState<CalculationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = useCallback(async () => {
    const amount = parseInputValue(taxableAmount);
    if (amount <= 0) {
      setError("Please enter a valid taxable income amount");
      return;
    }

    setError(null);
    setIsCalculating(true);

    try {
      const response = await payrollApi.calculateSalary({
        calculationType: "TAXABLE_AMOUNT",
        taxableAmount: amount,
      });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Calculation failed");
      setResult(null);
    } finally {
      setIsCalculating(false);
    }
  }, [taxableAmount]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleCalculate();
    },
    [handleCalculate]
  );

  const insuranceReliefAmount = parseInputValue(insuranceRelief);
  const payeDue = result
    ? Math.max(result.taxDetails.netPaye - insuranceReliefAmount, 0)
    : 0;

  return (
    <div className="space-y-8">
      {/* Input section */}
      <div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
              Taxable pay (KES)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={taxableAmount}
              onChange={(e) => setTaxableAmount(formatInputValue(e.target.value))}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 179,000"
              className="w-full border-b-2 border-[#DCE3EA] bg-transparent pb-2 font-[family-name:var(--font-source-serif)] text-[28px] font-medium text-slate-950 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
            />
          </div>
          <div>
            <label className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
              Insurance relief (KES)
              <span className="ml-2 normal-case tracking-normal text-slate-300">optional</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={insuranceRelief}
              onChange={(e) => setInsuranceRelief(formatInputValue(e.target.value))}
              onKeyDown={handleKeyDown}
              placeholder="0.00"
              className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
            />
          </div>
        </div>

        <button
          onClick={handleCalculate}
          disabled={isCalculating || !taxableAmount}
          className="ink-button mt-8 w-full border border-slate-950 bg-slate-950 px-5 py-3 text-[13px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isCalculating ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Calculating…
            </span>
          ) : (
            "Calculate PAYE"
          )}
        </button>

        {error && (
          <div className="mt-5 flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div>
          {/* PAYE Summary — ledger style */}
          <div className="square-card border border-[#DCE3EA] bg-white">
            <div className="border-b border-[#DCE3EA] px-5 py-3">
              <span className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">PAYE summary</span>
            </div>
            <div className="px-5">
              <div className="grid grid-cols-[1fr_120px] gap-3 border-b border-[#E8E7E1] py-3 text-[13px]">
                <span className="text-slate-600">Taxable Pay</span>
                <span className="mono text-right font-medium text-slate-900">{fmt(result.taxDetails.taxableIncome)}</span>
              </div>
              <div className="grid grid-cols-[1fr_120px] gap-3 border-b border-[#E8E7E1] py-3 text-[13px]">
                <span className="text-slate-600">PAYE Before Relief</span>
                <span className="mono text-right font-medium text-slate-900">{fmt(result.taxDetails.grossPaye)}</span>
              </div>
              <div className="grid grid-cols-[1fr_120px] gap-3 border-b border-[#E8E7E1] py-3 text-[13px]">
                <span className="text-slate-600">Personal Relief</span>
                <span className="mono text-right font-medium text-[#0F7B4D]">−{fmt(result.taxDetails.personalRelief)}</span>
              </div>
              <div className="grid grid-cols-[1fr_120px] gap-3 border-b border-[#E8E7E1] py-3 text-[13px]">
                <span className="text-slate-600">Insurance Relief</span>
                <span className="mono text-right font-medium text-[#0F7B4D]">−{fmt(insuranceReliefAmount)}</span>
              </div>
            </div>
            <div className="border-t-2 border-slate-950 px-5 py-4">
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <span className="text-[13px] font-medium text-slate-950">PAYE Due</span>
                <span className="font-[family-name:var(--font-source-serif)] text-right text-xl font-medium text-slate-950">{fmt(payeDue)}</span>
              </div>
            </div>
          </div>

          {/* Tax Band Breakdown Table */}
          {result.taxBandBreakdown && result.taxBandBreakdown.length > 0 && (
            <div className="square-card mt-6 border border-[#DCE3EA] bg-white overflow-hidden">
              <div className="border-b border-[#DCE3EA] px-5 py-3">
                <span className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Tax band breakdown</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="mono border-b border-[#DCE3EA] text-left text-[9px] uppercase tracking-[.08em] text-slate-400">
                      <th className="px-5 py-3 font-normal">From</th>
                      <th className="px-5 py-3 font-normal">To</th>
                      <th className="px-5 py-3 text-right font-normal">Taxable Pay</th>
                      <th className="px-5 py-3 text-right font-normal">Rate</th>
                      <th className="px-5 py-3 text-right font-normal">Tax Charged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.taxBandBreakdown.map((band, idx) => (
                      <tr
                        key={idx}
                        className={`border-b border-[#E8E7E1] ${
                          band.taxablePay > 0 ? "text-slate-700" : "text-slate-300"
                        }`}
                      >
                        <td className="px-5 py-3 tabular-nums mono">{fmt(band.from)}</td>
                        <td className="px-5 py-3 tabular-nums mono">{band.to != null ? fmt(band.to) : "—"}</td>
                        <td className="px-5 py-3 text-right tabular-nums mono font-medium">{fmt(band.taxablePay)}</td>
                        <td className="px-5 py-3 text-right tabular-nums mono">{fmtPct(band.rate)}</td>
                        <td className="px-5 py-3 text-right tabular-nums mono font-medium">{fmt(band.taxCharged)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-950 font-medium text-slate-950">
                      <td className="px-5 py-3" colSpan={4}>PAYE Before Relief</td>
                      <td className="px-5 py-3 text-right tabular-nums mono">{fmt(result.taxDetails.grossPaye)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
